import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PeriodComparisonFilter, {
    PeriodSelection,
} from '@/components/PeriodComparisonFilter'
import { IconButton, Typogaphy } from 'vnetwork-ui'
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts'
import ChartIcons from '@/icons/ChartIcons'
import GridIcons from '@/icons/GridIcons'
import dayjs from 'dayjs'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getBusinessPerformance } from '../../../services'
import { BusinessPerformanceParams } from '../../../types'
import { CHART_THEME } from '@/constants/chartColors'

interface MetricConfig {
    key: string
    label: string
    color: string
}

interface ComparisonProps {
    metrics: MetricConfig[]
}

const Comparison: React.FC<ComparisonProps> = ({ metrics }) => {
    const { t } = useTranslation()
    const [comparisonViewMode, setComparisonViewMode] = useState<
        'chart' | 'table'
    >('chart')

    // Period comparison filter state
    const [periodSelection, setPeriodSelection] = useState<PeriodSelection>({
        startDate: dayjs()
            .subtract(1, 'year')
            .startOf('year')
            .format('YYYY-MM-DD'),
        endDate: dayjs()?.endOf('year').format('YYYY-MM-DD'),
        viewMode: 'quarter',
        selectedPeriods: new Set([
            'quarter_1',
            'quarter_2',
            'quarter_3',
            'quarter_4',
        ]),
        selectedMetrics: new Set(metrics.map((m) => m.key)),
    })

    // Fetch data based on periodSelection
    const params: BusinessPerformanceParams = useMemo(
        () => ({
            from_month: periodSelection.startDate,
            to_month: periodSelection.endDate,
            tax_rate: 0.5,
            include_projected: true,
            breakdown: false,
        }),
        [periodSelection.startDate, periodSelection.endDate]
    )

    const { data, renderState } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.REPORTS_BI_BUSINESS_PERFORMANCE_COMPARISON,
            params,
        ],
        queryFn: () => getBusinessPerformance(params),
        staleTime: 5 * 60 * 1000,
    })

    // Prepare chart data from API response
    const chartData = useMemo(() => {
        if (!data) return []

        return data.map((item) => ({
            month: item.report_month,
            net_revenue: item.net_revenue,
            projected_revenue: item.projected_revenue || 0,
            net_other_income: item.net_other_income,
            gross_profit: item.gross_profit,
            operating_profit: item.operating_profit,
            profit_before_tax: item.profit_before_tax,
            net_profit: item.net_profit,
        }))
    }, [data])

    // Prepare comparison data
    const comparisonData = useMemo(() => {
        if (!chartData.length) return { years: [], quarters: [], months: [] }

        // Group data by year, quarter, and month
        const yearlyData: Record<string, { [key: string]: number }> = {}
        const quarterlyData: Record<string, { [key: string]: number }> = {}
        const monthlyData: Record<string, { [key: string]: number }> = {}

        chartData.forEach((item) => {
            const [year, month] = item.month.split('-')
            const monthNum = parseInt(month)
            const quarter = Math.ceil(monthNum / 3)
            const quarterKey = `Q${quarter} ${year}`
            const monthKey = `T${monthNum} ${year}`

            // Yearly aggregation
            if (!yearlyData[year]) {
                yearlyData[year] = {}
                metrics.forEach((metric) => {
                    yearlyData[year][metric.key] = 0
                })
            }
            metrics.forEach((metric) => {
                const value = item[metric.key as keyof typeof item]
                yearlyData[year][metric.key] +=
                    typeof value === 'number' ? value : 0
            })

            // Quarterly aggregation
            if (!quarterlyData[quarterKey]) {
                quarterlyData[quarterKey] = {}
                metrics.forEach((metric) => {
                    quarterlyData[quarterKey][metric.key] = 0
                })
            }
            metrics.forEach((metric) => {
                const value = item[metric.key as keyof typeof item]
                quarterlyData[quarterKey][metric.key] +=
                    typeof value === 'number' ? value : 0
            })

            // Monthly data
            monthlyData[monthKey] = {}
            metrics.forEach((metric) => {
                const value = item[metric.key as keyof typeof item]
                monthlyData[monthKey][metric.key] =
                    typeof value === 'number' ? value : 0
            })
        })

        return {
            years: Object.keys(yearlyData)
                .sort()
                .map((year) => ({
                    period: year,
                    data: yearlyData[year],
                })),
            quarters: Object.keys(quarterlyData)
                .sort((a, b) => {
                    const [qA, yearA] = a.split(' ')
                    const [qB, yearB] = b.split(' ')
                    if (yearA !== yearB) return yearA.localeCompare(yearB)
                    return qA.localeCompare(qB)
                })
                .map((quarter) => ({
                    period: quarter,
                    data: quarterlyData[quarter],
                })),
            months: Object.keys(monthlyData)
                .sort((a, b) => {
                    const [tA, yearA] = a.split(' ')
                    const [tB, yearB] = b.split(' ')
                    const monthA = parseInt(tA.replace('T', ''))
                    const monthB = parseInt(tB.replace('T', ''))
                    if (yearA !== yearB) return yearA.localeCompare(yearB)
                    return monthA - monthB
                })
                .map((month) => ({
                    period: month,
                    data: monthlyData[month],
                })),
        }
    }, [chartData, metrics])

    // Filter comparison data based on periodSelection
    const filteredComparisonData = useMemo(() => {
        if (periodSelection.viewMode === 'year') {
            return {
                years: comparisonData.years.filter((y) =>
                    periodSelection.selectedPeriods.has(y.period)
                ),
                quarters: [],
                months: [],
            }
        } else if (periodSelection.viewMode === 'quarter') {
            return {
                years: [],
                quarters: comparisonData.quarters.filter((q) => {
                    const quarter = q.period.split(' ')[0] // Get 'Q1', 'Q2', etc
                    return periodSelection.selectedPeriods.has(quarter)
                }),
                months: [],
            }
        } else {
            // month mode
            return {
                years: [],
                quarters: [],
                months: comparisonData.months.filter((m) => {
                    const monthNum = m.period.split(' ')[0].replace('T', '')
                    return periodSelection.selectedPeriods.has(monthNum)
                }),
            }
        }
    }, [comparisonData, periodSelection])

    // Prepare chart data for comparison
    const comparisonChartData = useMemo(() => {
        let periods: { period: string; data: { [key: string]: number } }[] = []

        if (periodSelection.viewMode === 'year') {
            periods = filteredComparisonData.years
        } else if (periodSelection.viewMode === 'quarter') {
            periods = filteredComparisonData.quarters.slice(-8)
        } else {
            periods = filteredComparisonData.months.slice(-12)
        }

        return periods.map((p) => {
            const item: any = { period: p.period }
            metrics.forEach((metric) => {
                if (periodSelection.selectedMetrics.has(metric.key)) {
                    item[metric.key] = p.data[metric.key]
                }
            })
            return item
        })
    }, [filteredComparisonData, periodSelection, metrics])

    // Calculate growth percentage
    const calculateGrowth = (current: number, previous: number) => {
        if (previous === 0) return 0
        return ((current - previous) / Math.abs(previous)) * 100
    }

    // Format percentage
    const formatPercentage = (value: number) => {
        const formatted = value.toFixed(1)
        return value > 0 ? `+${formatted}%` : `${formatted}%`
    }

    // Format number for display
    const formatNumber = (num: number) => {
        return new Intl.NumberFormat('vi-VN', {
            maximumFractionDigits: 0,
        }).format(num)
    }

    // Show loading/error states
    const stateComponent = renderState()
    if (stateComponent) return <>{stateComponent}</>

    return (
        <>
            {/* Period Comparison Filter */}
            <PeriodComparisonFilter
                metrics={metrics}
                onChange={setPeriodSelection}
                defaultStartDate={dayjs()?.startOf('year').format('YYYY-MM-DD')}
                defaultEndDate={dayjs()?.endOf('year').format('YYYY-MM-DD')}
            />

            <div className="bg-white border border-gray-200 rounded-lg p-6">
                {/* Header with view toggle */}
                <div className="flex items-center justify-between mb-4">
                    <Typogaphy className="font-medium text-base text-gray-800">
                        {t('period_comparison')}
                    </Typogaphy>
                    <div className="flex gap-2">
                        <IconButton
                            size="small"
                            color={
                                comparisonViewMode === 'chart'
                                    ? 'primary'
                                    : 'default'
                            }
                            onClick={() => setComparisonViewMode('chart')}
                            className="border"
                        >
                            <ChartIcons />
                        </IconButton>
                        <IconButton
                            size="small"
                            color={
                                comparisonViewMode === 'table'
                                    ? 'primary'
                                    : 'default'
                            }
                            onClick={() => setComparisonViewMode('table')}
                            className="border"
                        >
                            <GridIcons />
                        </IconButton>
                    </div>
                </div>

                {/* Chart View */}
                {comparisonViewMode === 'chart' &&
                    comparisonChartData.length > 0 && (
                        <ResponsiveContainer width="100%" height={500}>
                            <BarChart
                                data={comparisonChartData}
                                margin={{
                                    top: 20,
                                    right: 30,
                                    left: 20,
                                    bottom: 5,
                                }}
                            >
                                <CartesianGrid
                                    stroke={CHART_THEME.grid.stroke}
                                    strokeDasharray={
                                        CHART_THEME.grid.strokeDasharray
                                    }
                                />
                                <XAxis
                                    dataKey="period"
                                    tick={{
                                        fontSize: CHART_THEME.axis.fontSize,
                                        fontFamily: CHART_THEME.axis.fontFamily,
                                    }}
                                    stroke={CHART_THEME.axis.stroke}
                                />
                                <YAxis
                                    tickFormatter={(value) =>
                                        new Intl.NumberFormat('vi-VN', {
                                            notation: 'compact',
                                            compactDisplay: 'short',
                                        }).format(value)
                                    }
                                    tick={{
                                        fontSize: CHART_THEME.axis.fontSize,
                                        fontFamily: CHART_THEME.axis.fontFamily,
                                    }}
                                    stroke={CHART_THEME.axis.stroke}
                                />
                                <Tooltip
                                    contentStyle={
                                        CHART_THEME.tooltip.contentStyle
                                    }
                                    itemStyle={CHART_THEME.tooltip.itemStyle}
                                    labelStyle={CHART_THEME.tooltip.labelStyle}
                                    formatter={(value: number) =>
                                        formatNumber(value)
                                    }
                                />
                                <Legend
                                    wrapperStyle={{
                                        fontSize: CHART_THEME.legend.fontSize,
                                        fontFamily:
                                            CHART_THEME.legend.fontFamily,
                                    }}
                                />

                                {metrics
                                    .filter((m) =>
                                        periodSelection.selectedMetrics.has(
                                            m.key
                                        )
                                    )
                                    .map((metric) => (
                                        <Bar
                                            key={metric.key}
                                            dataKey={metric.key}
                                            name={metric.label}
                                            fill={metric.color}
                                        />
                                    ))}
                            </BarChart>
                        </ResponsiveContainer>
                    )}

                {/* Table View */}
                {comparisonViewMode === 'table' && (
                    <>
                        {/* Year Comparison */}
                        {filteredComparisonData.years.length > 0 && (
                            <div className="mb-6">
                                <h4 className="text-sm font-semibold text-gray-700 mb-3">
                                    {t('year_over_year')}
                                </h4>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="border-b border-gray-200">
                                                <th className="text-left py-2 px-3 font-semibold text-gray-600">
                                                    {t('metric')}
                                                </th>
                                                {filteredComparisonData.years.map(
                                                    (year, idx) => (
                                                        <th
                                                            key={year.period}
                                                            className="text-right py-2 px-3 font-semibold text-gray-600"
                                                        >
                                                            {year.period}
                                                            {idx > 0 && (
                                                                <span className="ml-2 text-xs text-gray-500">
                                                                    (vs{' '}
                                                                    {
                                                                        filteredComparisonData
                                                                            .years[
                                                                            idx -
                                                                                1
                                                                        ].period
                                                                    }
                                                                    )
                                                                </span>
                                                            )}
                                                        </th>
                                                    )
                                                )}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {metrics
                                                .filter((m) =>
                                                    periodSelection.selectedMetrics.has(
                                                        m.key
                                                    )
                                                )
                                                .map((metric) => (
                                                    <tr
                                                        key={metric.key}
                                                        className="border-b border-gray-100"
                                                    >
                                                        <td className="py-2 px-3 font-medium">
                                                            {metric.label}
                                                        </td>
                                                        {filteredComparisonData.years.map(
                                                            (year, idx) => {
                                                                const value =
                                                                    year.data[
                                                                        metric
                                                                            .key
                                                                    ]
                                                                const prevValue =
                                                                    idx > 0
                                                                        ? filteredComparisonData
                                                                              .years[
                                                                              idx -
                                                                                  1
                                                                          ]
                                                                              .data[
                                                                              metric
                                                                                  .key
                                                                          ]
                                                                        : null
                                                                const growth =
                                                                    prevValue !==
                                                                    null
                                                                        ? calculateGrowth(
                                                                              value,
                                                                              prevValue
                                                                          )
                                                                        : null

                                                                return (
                                                                    <td
                                                                        key={
                                                                            year.period
                                                                        }
                                                                        className="text-right py-2 px-3"
                                                                    >
                                                                        <div>
                                                                            {formatNumber(
                                                                                value
                                                                            )}
                                                                        </div>
                                                                        {growth !==
                                                                            null && (
                                                                            <div
                                                                                className={`text-xs ${
                                                                                    growth >
                                                                                    0
                                                                                        ? 'text-green-600'
                                                                                        : growth <
                                                                                            0
                                                                                          ? 'text-red-600'
                                                                                          : 'text-gray-500'
                                                                                }`}
                                                                            >
                                                                                {formatPercentage(
                                                                                    growth
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                    </td>
                                                                )
                                                            }
                                                        )}
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Quarter Comparison */}
                        {filteredComparisonData.quarters.length > 0 && (
                            <div className="mb-6">
                                <h4 className="text-sm font-semibold text-gray-700 mb-3">
                                    {t('quarter_over_quarter')}
                                </h4>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="border-b border-gray-200">
                                                <th className="text-left py-2 px-3 font-semibold text-gray-600">
                                                    {t('metric')}
                                                </th>
                                                {filteredComparisonData.quarters
                                                    .slice(-8)
                                                    .map(
                                                        (quarter, idx, arr) => (
                                                            <th
                                                                key={
                                                                    quarter.period
                                                                }
                                                                className="text-right py-2 px-3 font-semibold text-gray-600 whitespace-nowrap"
                                                            >
                                                                {quarter.period}
                                                                {idx > 0 && (
                                                                    <span className="ml-1 text-xs text-gray-500">
                                                                        (vs{' '}
                                                                        {
                                                                            arr[
                                                                                idx -
                                                                                    1
                                                                            ]
                                                                                .period
                                                                        }
                                                                        )
                                                                    </span>
                                                                )}
                                                            </th>
                                                        )
                                                    )}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {metrics
                                                .filter((m) =>
                                                    periodSelection.selectedMetrics.has(
                                                        m.key
                                                    )
                                                )
                                                .map((metric) => (
                                                    <tr
                                                        key={metric.key}
                                                        className="border-b border-gray-100"
                                                    >
                                                        <td className="py-2 px-3 font-medium">
                                                            {metric.label}
                                                        </td>
                                                        {filteredComparisonData.quarters
                                                            .slice(-8)
                                                            .map(
                                                                (
                                                                    quarter,
                                                                    idx,
                                                                    arr
                                                                ) => {
                                                                    const value =
                                                                        quarter
                                                                            .data[
                                                                            metric
                                                                                .key
                                                                        ]
                                                                    const prevValue =
                                                                        idx > 0
                                                                            ? arr[
                                                                                  idx -
                                                                                      1
                                                                              ]
                                                                                  .data[
                                                                                  metric
                                                                                      .key
                                                                              ]
                                                                            : null
                                                                    const growth =
                                                                        prevValue !==
                                                                        null
                                                                            ? calculateGrowth(
                                                                                  value,
                                                                                  prevValue
                                                                              )
                                                                            : null

                                                                    return (
                                                                        <td
                                                                            key={
                                                                                quarter.period
                                                                            }
                                                                            className="text-right py-2 px-3"
                                                                        >
                                                                            <div>
                                                                                {formatNumber(
                                                                                    value
                                                                                )}
                                                                            </div>
                                                                            {growth !==
                                                                                null && (
                                                                                <div
                                                                                    className={`text-xs ${
                                                                                        growth >
                                                                                        0
                                                                                            ? 'text-green-600'
                                                                                            : growth <
                                                                                                0
                                                                                              ? 'text-red-600'
                                                                                              : 'text-gray-500'
                                                                                    }`}
                                                                                >
                                                                                    {formatPercentage(
                                                                                        growth
                                                                                    )}
                                                                                </div>
                                                                            )}
                                                                        </td>
                                                                    )
                                                                }
                                                            )}
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Month Comparison */}
                        {filteredComparisonData.months.length > 0 && (
                            <div>
                                <h4 className="text-sm font-semibold text-gray-700 mb-3">
                                    {t('month_over_month')}
                                </h4>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="border-b border-gray-200">
                                                <th className="text-left py-2 px-3 font-semibold text-gray-600">
                                                    {t('metric')}
                                                </th>
                                                {filteredComparisonData.months
                                                    .slice(-12)
                                                    .map((month, idx, arr) => (
                                                        <th
                                                            key={month.period}
                                                            className="text-right py-2 px-3 font-semibold text-gray-600 whitespace-nowrap"
                                                        >
                                                            {month.period}
                                                            {idx > 0 && (
                                                                <span className="ml-1 text-xs text-gray-500">
                                                                    (vs{' '}
                                                                    {
                                                                        arr[
                                                                            idx -
                                                                                1
                                                                        ].period
                                                                    }
                                                                    )
                                                                </span>
                                                            )}
                                                        </th>
                                                    ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {metrics
                                                .filter((m) =>
                                                    periodSelection.selectedMetrics.has(
                                                        m.key
                                                    )
                                                )
                                                .map((metric) => (
                                                    <tr
                                                        key={metric.key}
                                                        className="border-b border-gray-100"
                                                    >
                                                        <td className="py-2 px-3 font-medium">
                                                            {metric.label}
                                                        </td>
                                                        {filteredComparisonData.months
                                                            .slice(-12)
                                                            .map(
                                                                (
                                                                    month,
                                                                    idx,
                                                                    arr
                                                                ) => {
                                                                    const value =
                                                                        month
                                                                            .data[
                                                                            metric
                                                                                .key
                                                                        ]
                                                                    const prevValue =
                                                                        idx > 0
                                                                            ? arr[
                                                                                  idx -
                                                                                      1
                                                                              ]
                                                                                  .data[
                                                                                  metric
                                                                                      .key
                                                                              ]
                                                                            : null
                                                                    const growth =
                                                                        prevValue !==
                                                                        null
                                                                            ? calculateGrowth(
                                                                                  value,
                                                                                  prevValue
                                                                              )
                                                                            : null

                                                                    return (
                                                                        <td
                                                                            key={
                                                                                month.period
                                                                            }
                                                                            className="text-right py-2 px-3"
                                                                        >
                                                                            <div>
                                                                                {formatNumber(
                                                                                    value
                                                                                )}
                                                                            </div>
                                                                            {growth !==
                                                                                null && (
                                                                                <div
                                                                                    className={`text-xs ${
                                                                                        growth >
                                                                                        0
                                                                                            ? 'text-green-600'
                                                                                            : growth <
                                                                                                0
                                                                                              ? 'text-red-600'
                                                                                              : 'text-gray-500'
                                                                                    }`}
                                                                                >
                                                                                    {formatPercentage(
                                                                                        growth
                                                                                    )}
                                                                                </div>
                                                                            )}
                                                                        </td>
                                                                    )
                                                                }
                                                            )}
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </>
    )
}

export default Comparison
